<script setup lang="ts">
import { ref, onMounted, nextTick } from "vue";
import {
  getCategoryRoute,
  getTranslatedProperty,
} from "@shopware-pwa/helpers-next";
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);

const props = defineProps<{
  recipeData: any;
}>();
const recipe = props.recipeData;
const { t } = useI18n();

const printPdf = ref(false);

function getTime(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  let result = "";
  if (hours > 0) {
    result += t("recipe.time.hour", { count: hours });
  }
  if (remainingMinutes > 0) {
    if (hours > 0) {
      result += " ";
    }
    result += t("recipe.time.minute", { count: remainingMinutes });
  }

  return result;
}

const showShareModal = ref(false);

// Function to share via Web Share API
const shareViaAPI = async () => {
  const shareData = {
    title: document.title,
    url: window.location.href,
    text: t("recipe.shareContent"),
  };

  try {
    if (navigator.share) {
      await navigator.share(shareData);
    } else {
      await navigator.clipboard.writeText(window.location.href);
      alert(t("messages.linkCopied"));
      showShareModal.value = true;
    }
  } catch (error) {
    console.error("Error sharing:", error);
    showShareModal.value = true;
  }
};

const html2pdfs = ref<any>(null);
onMounted(async () => {
  html2pdfs.value = await import("html2pdf.js");
});

const downloadPDF = () => {
  printPdf.value = true;
  const element = document.querySelector(".recipies-to-print"); // Target the section you want to export
  if (!element) return;
  const html2pdf = html2pdfs.value.default;
  html2pdf()
    .set({
      margin: 2,
      filename: `recipe.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 1 },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
      },
    })
    .from(element)
    .save()
    .then(() => {
      printPdf.value = false;
    });
};

const activeAccordion = ref<number | null>(null);
const toggleAccordion = (index: number) => {
  activeAccordion.value = activeAccordion.value === index ? null : index;
};

const isMobile = ref(false);

const checkMobile = () => {
  isMobile.value = window.innerWidth < 640;
};

// Check if we're on mobile
onMounted(() => {
  checkMobile();
  window.addEventListener("resize", checkMobile);
});
</script>

<template>
  <section>
    <div class="recepies-detail px-4">
      <div class="container mx-auto py-8 sm:pb-24">
        <div class="flex justify-between flex-wrap sm:flex-nowrap sm:flex-row">
          <h1
            v-if="isMobile"
            class="text-2xl md:text-3xl font-bold mb-4 md:mb-6 lg:mb-9 mb-6"
          >
            {{ recipe?.title }}
          </h1>
          <div class="w-full rounded-lg mb-8 lg:mb-0">
            <NuxtImg
              :src="recipe?.cover?.media?.url"
              :alt="recipe?.title"
              :width="420"
              :height="420"
              loading="lazy"
              format="webp"
              class="w-full rounded-lg h-auto sm:w-[300px] sm:h-[300px] lg:w-full lg:h-auto object-cover"
            />
          </div>
          <!-- Content Section -->
          <div class="w-full p-0 lg:pl-[6%]">
            <!-- Product Title -->
            <h1
              class="text-2xl md:text-3xl font-bold mb-4 md:mb-6 lg:mb-9 hidden sm:block"
            >
              {{ recipe?.title }}
            </h1>

            <!-- Icon Row (3 columns) -->
            <div
              class="grid grid-cols-1 gap-4 mb-4 md:mb-6 lg:mb-8 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3"
            >
              <div class="flex items-start" v-if="recipe?.portions">
                <div
                  class="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-snow rounded-full mr-4"
                >
                  <img src="assets/icon/user-red-icon.svg" alt="Icon 1" />
                </div>
                <div>
                  <p class="text-xs md:text-sm">
                    {{ $t("recipe.servings") }}
                  </p>
                  <p class="text-sm md:text-base font-bold nowarp">
                    {{ recipe?.portions }} {{ $t("recipe.persons") }}
                  </p>
                </div>
              </div>

              <div class="flex items-start" v-if="recipe?.totalTime">
                <div
                  class="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-snow rounded-full mr-4"
                >
                  <img src="assets/icon/clock-red-icon.svg" alt="Icon 2" />
                </div>
                <div>
                  <p class="text-xs md:text-sm">
                    {{ $t("recipe.cookTime") }}
                  </p>
                  <p class="text-sm md:text-base font-bold">
                    {{ getTime(recipe?.totalTime) }}
                  </p>
                </div>
              </div>

              <div class="flex items-start" v-if="recipe?.difficulty">
                <div
                  class="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-snow rounded-full mr-4"
                >
                  <img src="assets/icon/process-red-icon.svg" alt="Icon 3" />
                </div>
                <div>
                  <p class="text-xs md:text-sm">
                    {{ $t("recipe.difficulty") }}
                  </p>
                  <p
                    class="text-sm md:text-base font-bold"
                    v-if="recipe?.difficulty"
                  >
                    {{ $t("recipe.content.difficulty." + recipe?.difficulty) }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Separator Line -->
            <hr class="border-t border-gray-300 mb-4 md:mb-6 lg:mb-8" />
            <!-- Ingredients Section -->
            <h5 class="text-lg md:text-xl font-bold mb-3 md:mb-4">
              {{ $t("recipe.ingredients") }}
            </h5>
            <template
              v-if="recipe?.ingredientLists"
              v-for="ingredients in recipe?.ingredientLists"
            >
              <ul
                class="mb-6 md:mb-8 leading-tight text-sm lg:text-base font-semibold text-dark-bg"
              >
                <li class="text-lg font-bold mb-3 md:mb-4">
                  <img
                    class="max-w-[20px] mr-2 inline-block"
                    src="assets/icon/recipe.png"
                    alt="circle icon"
                  />
                  {{ ingredients?.translated?.title }}
                </li>
                <template v-for="ingredientItem in ingredients.ingredients">
                  <li
                    v-if="ingredientItem.title"
                    class="mb-4 flex items-center"
                  >
                    <img
                      src="assets/icon/correct-with-circle-icon.svg"
                      alt="circle icon"
                    />
                    <span class="ml-3">
                      {{ ingredientItem.quantity }}
                      <template v-if="$te(ingredientItem.unit)">
                        {{ $t(ingredientItem.unit) }}
                      </template>
                      <template v-else>
                        {{ ingredientItem.unit }}
                      </template>
                      {{ ingredientItem.title }}
                    </span>
                  </li>
                </template>
              </ul>
            </template>
            <!-- Separator Line -->
            <hr class="border-t border-gray-300 mb-4 md:mb-6 lg:mb-8" />

            <!-- Tags Section -->
            <h5
              class="text-lg md:text-xl font-bold mb-1 md:mb-2"
              v-if="recipe?.categories"
            >
              {{ $t("recipe.tags") }}
            </h5>
            <p
              v-if="recipe?.categories"
              class="mb-6 md:mb-8 text-sm lg:text-base font-semibold text-red-bg"
            >
              <template v-for="category in recipe?.categories">
                <NuxtLink
                  role="menuitem"
                  :to="formatLink(getCategoryRoute(category))"
                >
                  {{ getTranslatedProperty(category, "name") }}
                </NuxtLink>
                <span
                  v-if="
                    category !==
                    recipe?.categories[recipe?.categories.length - 1]
                  "
                  >,</span
                >
              </template>
            </p>

            <!-- Buttons Section -->
            <div
              class="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0"
            >
              <button
                class="btn-default-px-6 btn-red-bg btn-w-icon"
                :disabled="printPdf"
                @click="downloadPDF"
              >
                <span v-if="!printPdf">
                  {{ $t("recipe.downloadRecipe") }}
                </span>
                <span v-else>
                  {{ $t("recipe.processingRecipe") }}
                </span>
                <img
                  src="assets/icon/download-icon.svg"
                  class="w-5 h-5 ml-2"
                  alt="Download Icon"
                />
              </button>
              <button
                class="btn-default-px-6 btn-w-icon btn-transprent-bg"
                @click="shareViaAPI"
              >
                {{ $t("recipe.shareRecipe") }}
                <span class="w-5 h-5 ml-2"
                  ><i class="fa-solid fa-arrow-up-from-bracket"></i
                ></span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        class="instruction px-4 bg-c-light-grey"
        :class="{
          'instruction-print': !isMobile,
        }"
      >
        <div class="container mx-auto relative py-12 sm:py-20">
          <div class="title mb-4 text-center mb-12">
            <h4 class="text-2xl md:text-3xl lg:text-4xl font-bold">
              {{ $t("recipe.cooking") }}
              <span class="text-red-bg">{{ $t("recipe.instructions") }}</span>
            </h4>
          </div>
          <div
            class="wrap w-full mx-auto bg-white rounded-2xl p-9 md:w-[794px]"
          >
            <div class="accordion">
              <div
                v-for="(preparations, index) in recipe?.preparations"
                :key="index"
                class="accordion-item border-b border-custom-grey bg-white"
              >
                <button
                  class="accordion-header flex justify-between items-center w-full py-5 focus:outline-none bg-white"
                  @click="toggleAccordion(index)"
                >
                  <span
                    class="text-lg lg:text-xl font-bold text-red-bg leading-normal uppercase"
                    >{{ preparations?.title }}</span
                  >
                  <svg
                    class="w-2 h-2 transform transition-transform accordion-icon"
                    viewBox="0 0 8 5"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    :class="{
                      'rotate-180': activeAccordion === index,
                    }"
                  >
                    <path
                      d="M3.51984 4.17398L0.368716 1.02288C0.325249 0.979409 0.291016 0.930875 0.266016 0.877275C0.241016 0.823658 0.228516 0.766217 0.228516 0.70495C0.228516 0.582417 0.269949 0.475959 0.352816 0.385576C0.435666 0.295193 0.544874 0.25 0.680441 0.25H7.31884C7.45441 0.25 7.56362 0.295626 7.64647 0.386876C7.72933 0.478143 7.77076 0.584609 7.77076 0.706276C7.77076 0.736709 7.72397 0.842309 7.63037 1.02308L4.47944 4.174C4.40701 4.24643 4.33213 4.29932 4.25482 4.33265C4.17748 4.36598 4.09242 4.38265 3.99964 4.38265C3.90686 4.38265 3.8218 4.36598 3.74447 4.33265C3.66715 4.29932 3.59227 4.24643 3.51984 4.17398Z"
                      fill="#141414"
                    />
                  </svg>
                </button>

                <div
                  class="accordion-content pb-4 text-chinese-black block description-p"
                  :class="{
                    hidden: activeAccordion === index,
                  }"
                >
                  <template
                    v-for="steps in preparations?.steps"
                    class="accordion-item border-b border-custom-grey bg-white"
                  >
                    <div
                      class="text-base md:text-lg lg:text-xl font-normal leading-normal"
                      v-html="steps.description"
                    ></div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="recipies-to-print" :class="{ hidden: !printPdf }">
      <CommonRecipePrint v-if="recipe" :recipe-data="recipe" />
    </div>
    <CommonRecipeImageText
      v-if="recipe?.translated?.customFields"
      :box-data="recipe?.translated?.customFields"
      :video-image="recipe?.video_image"
    />

    <CommonRecipeProductSlider
      v-if="recipe?.productRecommendations"
      :products="recipe?.productRecommendations"
    />
  </section>
</template>

<style scoped>
.recipies-to-print {
  page-break-inside: avoid;
}

.instruction-print {
  page-break-before: always;
}
</style>
